// Small shared pieces: page frame, loading and error states, warnings, and the signed-in caller.

import { createContext, useContext, useState, type ReactNode } from "react";
import { MessageBar, MessageBarBody, Spinner, Subtitle1, Switch } from "@fluentui/react-components";
import type { Me } from "./api";

export const MeContext = createContext<Me | null>(null);

/** The signed-in caller. Screens use it to hide what the API would refuse anyway. */
export function useMe(): Me & { can: (capability: string) => boolean } {
  const me = useContext(MeContext);
  if (!me) throw new Error("useMe outside the signed-in app");
  return { ...me, can: (c: string) => me.capabilities.includes(c) };
}

export function Page({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="page">
      <header className="page-head">
        <Subtitle1 as="h1" className="page-title">
          {title}
        </Subtitle1>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </section>
  );
}

export const Loading = ({ label = "Loading…" }: { label?: string }) => <Spinner size="small" label={label} labelPosition="after" />;

export function ErrorBar({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <MessageBar intent="error">
      <MessageBarBody>{message}</MessageBarBody>
    </MessageBar>
  );
}

export function Warnings({ items, intent = "warning" }: { items: string[]; intent?: "warning" | "info" }) {
  if (!items.length) return null;
  return (
    <MessageBar intent={intent} layout="multiline">
      <MessageBarBody>
        {items.length === 1 ? (
          items[0]
        ) : (
          <ul className="plain-list">
            {items.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </MessageBarBody>
    </MessageBar>
  );
}

/** A headline figure. A tone colors it against a target, and the flag says so in words. */
export function Kpi({ label, value, sub, tone, flag }: { label: string; value: string; sub?: ReactNode; tone?: "good" | "bad"; flag?: string }) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className={`kpi-value ${tone ?? ""}`}>
        {value}
        {tone && <span className="kpi-flag">{flag ?? (tone === "good" ? "On target" : "Below target")}</span>}
      </span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  );
}

/** A figure with a caption above it. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="fact">
      <span className="fact-label">{label}</span>
      <span className="fact-value">{children}</span>
    </span>
  );
}

/**
 * Whether a screen shows archived people (Chris, 2026-10-15: active by default everywhere, as on
 * People). Remembered per screen in this browser; storage can be blocked, and then the choice lasts
 * until the page reloads.
 */
export function useShowArchived(screen: string): [boolean, (on: boolean) => void] {
  const key = `finance.${screen}.showArchived`;
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  const set = (next: boolean) => {
    setOn(next);
    try {
      localStorage.setItem(key, next ? "1" : "0");
    } catch {
      // blocked: kept for this page only
    }
  };
  return [on, set];
}

/** The switch itself, shown only when someone is archived. */
export function ArchivedSwitch({ count, on, onChange }: { count: number; on: boolean; onChange: (on: boolean) => void }) {
  if (count <= 0) return null;
  return <Switch label={`Show archived (${count})`} checked={on} onChange={(_, d) => onChange(d.checked)} />;
}
